import React from "react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardContent } from "ui/components/card";
import { DepartmentShape } from "./shapes/department-shape";

const LVATooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  const loggedPct = p.loggedPercentage ?? 0;
  const approvedPct = p.approvedPercentage ?? p.y ?? 0;
  return (
    <div className="rounded border bg-white p-2 text-xs shadow">
      <div className="font-medium mb-1">
        {p.department.name || "Department"}
      </div>
      <div>Logged (%): {Number(loggedPct).toFixed(0)}%</div>
      <div>Approved (%): {Number(approvedPct).toFixed(0)}%</div>
      <div className="mt-1 text-muted-foreground">
        <div>Payroll: {p.payrollHours ?? "-"} h</div>
        <div>Logged: {p.loggedHours ?? p.x ?? "-"} h</div>
        <div>Approved: {p.approvedHours ?? "-"} h</div>
      </div>
    </div>
  );
};

export default function LoggedVsApprovedScatter({
  data = [],
  loading = false,
  isError = false,
  error = null,
}) {


  const ticksPct = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  // Normalize data for new schema: use loggedPercentage on X and approvedVsPayrollPercentage on Y
  const chartData = Array.isArray(data)
    ? data.map((d) => {
      const payroll = Number(d.payrollHours) || 0;
      const loggedHrs = Number(d.loggedHours) || Number(d.x) || 0;
      const approvedHrs = Number(d.approvedHours) || 0;

      const loggedPercentage =
        d.loggedPercentage !== undefined
          ? Number(d.loggedPercentage)
          : payroll > 0
            ? (loggedHrs / payroll) * 100
            : 0;

      const approvedPercentage =
        d.approvedPercentage !== undefined
          ? Number(d.approvedPercentage)
          : payroll > 0
            ? (approvedHrs / payroll) * 100
            : Number(d.y) || 0;

      return {
        ...d,
        loggedPercentage,
        approvedPercentage,
      };
    })
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[500px]">
        <div className="text-sm text-muted-foreground">
          Loading chart data...
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-[500px]">
        <div className="text-sm text-red-500">
          {error?.message || "Failed to fetch chart data"}
        </div>
      </div>
    );
  }

  return (
    <Card className="w-full">
      <CardContent>
        <div className="h-[500px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 60, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                dataKey="loggedPercentage"
                name="Logged"
                unit="%"
                domain={[0, 100]}
                ticks={ticksPct}
                label={{
                  value: "Logged (%)",
                  position: "insideBottom",
                  offset: -10,
                }}
              />
              <YAxis
                type="number"
                dataKey="approvedPercentage"
                name="Approved"
                unit="%"
                domain={[0, 100]}
                label={{
                  value: "Approved (%)",
                  angle: -90,
                  position: "insideLeft",
                }}
                ticks={ticksPct}
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={<LVATooltip />}
              />
              <Scatter
                data={chartData}
                name="Departments"
                shape={<DepartmentShape />}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
